import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Logo, KenyaStripe } from "@/components/Logo";
import { Crown, ShieldCheck, Gavel, Users2, LogOut, ArrowRight, UserCircle2 } from "lucide-react";
import { roleHome } from "@/lib/roleHome";

const HERO = "/assets/login-court.jpg";

// Demo credentials are gated behind an env flag so production builds can hide them.
// Set REACT_APP_ENABLE_DEMO_LOGIN=false to remove the "Fill demo" / "1-click demo"
// affordances and the placeholder emails.
const DEMO_ENABLED = String(process.env.REACT_APP_ENABLE_DEMO_LOGIN || "true").toLowerCase() !== "false";

const ROLE_TABS = [
  {
    key: "super_admin",
    label: "Super Admin",
    short: "SA",
    accent: "#E32424",
    icon: Crown,
    blurb: "Full command — finance, config, deletion rights.",
    demo: DEMO_ENABLED ? { email: "admin@marapadel.com", password: "MaraAdmin2026!" } : null,
  },
  {
    key: "league_admin",
    label: "League Admin",
    short: "LA",
    accent: "#00A86B",
    icon: ShieldCheck,
    blurb: "Operations — schedules, rosters, jerseys.",
    demo: DEMO_ENABLED ? { email: "league@marapadel.com", password: "League2026!" } : null,
  },
  {
    key: "umpire",
    label: "Court Umpire",
    short: "UM",
    accent: "#F4B400",
    icon: Gavel,
    blurb: "Live scoring on assigned courts.",
    demo: DEMO_ENABLED ? { email: "umpire@marapadel.com", password: "Umpire2026!" } : null,
  },
  {
    key: "team_owner",
    label: "Team Owner",
    short: "TO",
    accent: "#7DD3FC",
    icon: Users2,
    blurb: "Bid for your roster · view your team.",
    demo: DEMO_ENABLED ? { email: "owner@marapadel.com", password: "Owner2026!" } : null,
  },
  {
    key: "player",
    label: "Player / Fan",
    short: "PL",
    accent: "#38BDF8",
    icon: UserCircle2,
    blurb: "Follow your team · watch live · get alerts.",
    demo: DEMO_ENABLED ? { email: "player@marapadel.com", password: "Player2026!" } : null,
  },
];

export default function Login() {
  const { login, logout, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [activeRole, setActiveRole] = useState("super_admin");
  const [emails, setEmails] = useState(() => Object.fromEntries(ROLE_TABS.map((r) => [r.key, ""])));
  const [passwords, setPasswords] = useState(() => Object.fromEntries(ROLE_TABS.map((r) => [r.key, ""])));
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  // If we just landed on /admin with an active session AND a `?from=...` redirect intent,
  // honour it (this is the post-login flow). Otherwise show the "session active" panel.
  React.useEffect(() => {
    if (user && loc.state?.from?.pathname) {
      nav(loc.state.from.pathname, { replace: true });
    }
  }, [user, nav, loc.state]);

  const landingFor = (u) => loc.state?.from?.pathname || roleHome(u?.role);

  const current = ROLE_TABS.find((r) => r.key === activeRole) || ROLE_TABS[0];

  const submit = async (e, presetEmail, presetPassword) => {
    e?.preventDefault();
    const em = presetEmail ?? emails[activeRole];
    const pw = presetPassword ?? passwords[activeRole];
    if (!em || !pw) return toast.error("Email and password are required");
    setLoading(true);
    const r = await login(em, pw);
    setLoading(false);
    if (r.ok) {
      toast.success(`Welcome — ${current.label}`);
      // Phase 7: role-based landing (referee → /referee, owner → /owner, spectator → /player)
      nav(loc.state?.from?.pathname || roleHome(r.user?.role), { replace: true });
    } else {
      toast.error(r.error || "Login failed");
    }
  };

  const fillDemo = () => {
    if (!current.demo) return;
    setEmails({ ...emails, [activeRole]: current.demo.email });
    setPasswords({ ...passwords, [activeRole]: current.demo.password });
  };

  const oneClickDemo = () => {
    if (!current.demo) return;
    submit(null, current.demo.email, current.demo.password);
  };

  const switchRole = async () => {
    setSwitching(true);
    await logout();
    setSwitching(false);
    toast.success("Signed out — pick a new role below");
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-[#060d09]">
      {/* Hero image side */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Padel court action" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-black via-black/70 to-transparent" />
        <div className="relative h-full p-12 flex flex-col justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#00A86B]">Season 03 · Sept 2026</div>
            <div className="mt-6"><Logo className="h-20 md:h-24 w-auto" /></div>
            <KenyaStripe className="mt-4 w-48" />
            <h1 className="font-anton mt-6 text-5xl xl:text-6xl uppercase tracking-tighter leading-[0.85] text-white">
              Mara Padel<br/>League.
            </h1>
            <p className="mt-6 text-zinc-300 max-w-md">
              Four roles. Four portals. One control room.
              Pick yours below to sign in to the right cockpit.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widest text-zinc-500">
            <span>Live Scoring</span><span>·</span><span>Randomised Auction</span><span>·</span><span>Master Config</span>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 lg:p-12 score-grid-bg">
        <Card className="w-full max-w-lg p-6 lg:p-8 bg-[#11241b] border-white/10">
          <div className="mb-6 lg:hidden flex items-center gap-3">
            <Logo className="h-12 w-auto" />
          </div>
          <div className="mb-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Select Your Portal</div>
            <h2 className="font-anton text-3xl uppercase tracking-tight text-white mt-1">Sign In By Role</h2>
            <KenyaStripe className="mt-3 w-24" />
          </div>

          {user && (
            <div
              className="mb-5 rounded-lg border border-[#00A86B]/40 bg-[#00A86B]/10 p-4"
              data-testid="login-active-session"
            >
              <div className="flex items-center gap-3">
                <UserCircle2 className="w-8 h-8 text-[#00A86B] shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-widest text-[#00A86B] font-bold">Already Signed In</div>
                  <div className="text-sm text-white font-bold truncate" data-testid="login-active-user-email">{user.email}</div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 capitalize" data-testid="login-active-user-role">{(user.role || "").replace("_", " ")}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <Button
                  asChild
                  size="sm"
                  className="bg-[#00A86B] hover:bg-[#00935A] text-white font-bold uppercase tracking-widest"
                  data-testid="login-continue-session"
                >
                  <Link to={landingFor(user)}>
                    Continue <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                  </Link>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={switchRole}
                  disabled={switching}
                  className="border-white/20 text-white hover:bg-white/10 font-bold uppercase tracking-widest"
                  data-testid="login-switch-role"
                >
                  <LogOut className="w-3.5 h-3.5 mr-1.5" />
                  {switching ? "Signing out…" : "Switch Role"}
                </Button>
              </div>
            </div>
          )}

          <Tabs value={activeRole} onValueChange={setActiveRole} data-testid="login-role-tabs">
            <TabsList className="grid grid-cols-4 w-full bg-[#060d09] border border-white/10 h-auto p-1">
              {ROLE_TABS.map((r) => {
                const Icon = r.icon;
                return (
                  <TabsTrigger
                    key={r.key}
                    value={r.key}
                    data-testid={`role-tab-${r.key}`}
                    className="flex flex-col items-center gap-1 py-2 text-[10px] uppercase tracking-widest text-zinc-400 data-[state=active]:bg-[#11241b] data-[state=active]:text-white"
                    style={{ "--tab-accent": r.accent }}
                  >
                    <Icon className="w-4 h-4" style={{ color: r.accent }} />
                    <span className="hidden sm:inline">{r.label}</span>
                    <span className="sm:hidden">{r.short}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {ROLE_TABS.map((r) => (
              <TabsContent key={r.key} value={r.key} className="mt-5" data-testid={`role-panel-${r.key}`}>
                <div
                  className="rounded-lg p-3 border text-xs flex items-start gap-2"
                  style={{ borderColor: `${r.accent}55`, background: `${r.accent}11` }}
                >
                  <r.icon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: r.accent }} />
                  <div>
                    <div className="font-bold text-white uppercase tracking-wide">{r.label} Portal</div>
                    <div className="text-zinc-400 mt-0.5">{r.blurb}</div>
                  </div>
                </div>

                <form onSubmit={submit} className="space-y-3 mt-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">Email</label>
                    <Input
                      data-testid={`login-email-${r.key}`}
                      value={emails[r.key]}
                      onChange={(e) => setEmails({ ...emails, [r.key]: e.target.value })}
                      placeholder={r.demo?.email || "your.email@example.com"}
                      type="email"
                      required
                      className="mt-1 bg-[#060d09] border-white/10 text-white"
                      style={{ outlineColor: r.accent }}
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-zinc-500">Password</label>
                    <Input
                      data-testid={`login-password-${r.key}`}
                      value={passwords[r.key]}
                      onChange={(e) => setPasswords({ ...passwords, [r.key]: e.target.value })}
                      type="password"
                      required
                      className="mt-1 bg-[#060d09] border-white/10 text-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid={`login-submit-${r.key}`}
                    disabled={loading}
                    className="w-full text-white font-bold uppercase tracking-widest h-11 hover:opacity-90"
                    style={{ backgroundColor: r.accent }}
                  >
                    {loading ? "Signing in…" : `Sign in as ${r.label}`}
                  </Button>
                </form>

                {r.demo && (
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                    <button
                      type="button"
                      onClick={fillDemo}
                      data-testid={`fill-demo-${r.key}`}
                      className="text-zinc-400 hover:text-white underline underline-offset-2"
                    >
                      Fill demo credentials
                    </button>
                    <button
                      type="button"
                      onClick={oneClickDemo}
                      data-testid={`one-click-demo-${r.key}`}
                      className="font-bold uppercase tracking-widest"
                      style={{ color: r.accent }}
                    >
                      1-click demo →
                    </button>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>

          <div className="mt-6 pt-5 border-t border-white/10 flex flex-wrap justify-between gap-2 text-xs">
            <a href="/live" data-testid="link-public-live" className="text-zinc-400 hover:text-white">→ Public Live Broadcast</a>
            <div className="flex gap-3">
              <Link to="/signup" data-testid="link-operator-signup" className="text-[#F4B400] hover:underline">Request Access →</Link>
              <Link to="/register-player" data-testid="link-register-player" className="text-[#00A86B] hover:underline">Register as Player →</Link>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
